import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { errorToast, successToast } from '../components/Toast';
import { Policy2, PolicyInvoice2Response } from '../types/policy';
import {
	PolicyholderCreateRequest,
	PolicyholderResponse,
	PolicyholderResponse2,
	PolicyholderUpdateRequest,
} from '../types/policyholder';
import { getApiUrl, hasOverdueInvoices } from '../util/util';
import { get, post } from './http';
import PolicyApi from './PolicyApi';

interface PolicyholderApi {
	createPolicyholder(
		authorizationToken: string,
		request: PolicyholderCreateRequest,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyholderResponse>>;
	updatePolicyholder(
		authorizationToken: string,
		request: PolicyholderUpdateRequest,
	): Promise<AxiosResponse<PolicyholderResponse>>;
	getPolicyholder(
		authorizationToken: string,
		locator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyholderResponse>>;
	getPolicyholders(
		authorizationToken: string,
		pagingToken: string,
		limit?: number,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyholderResponse2>>;
	getPoliciesForPolicyholder(
		authorizationToken: string,
		locator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<Policy2[]>>;
	getInvoicesForPolicyholder(
		authorizationToken: string,
		locator: string,
		config?: AxiosRequestConfig<any>,
	): Promise<AxiosResponse<PolicyInvoice2Response[]>>;
}

const PolicyholderApi = {
	createPolicyholder(
		authorizationToken: string,
		request: PolicyholderCreateRequest,
		config?: AxiosRequestConfig<any>,
	) {
		return post(
			`${getApiUrl()}/policyholder/create`,
			authorizationToken,
			request,
			config,
		);
	},

	updatePolicyholder(
		authorizationToken: string,
		request: PolicyholderUpdateRequest,
	) {
		return post(
			`${getApiUrl()}/policyholder/update`,
			authorizationToken,
			request,
		);
	},

	getPolicyholder(
		authorizationToken: string,
		policyholderLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policyholder/${policyholderLocator}`,
			authorizationToken,
			config,
		);
	},

	getPolicyholders(
		authorizationToken: string,
		pagingToken: string,
		limit: number,
		config?: AxiosRequestConfig<any>,
	) {
		const tokenString = pagingToken ? `?pagingToken=${pagingToken}` : '';

		return get(
			`${getApiUrl()}/policyholders${tokenString}`,
			authorizationToken,
			config,
		);
	},

	getPoliciesForPolicyholder(
		authorizationToken: string,
		policyholderLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policyholder/${policyholderLocator}/policies`,
			authorizationToken,
			config,
		);
	},

	getInvoicesForPolicyholder(
		authorizationToken: string,
		policyholderLocator: string,
		config?: AxiosRequestConfig<any>,
	) {
		return get(
			`${getApiUrl()}/policyholder/${policyholderLocator}/invoices`,
			authorizationToken,
			config,
		);
	},
} as PolicyholderApi;

export default PolicyholderApi;

export const getUsePoliciesForPolicyholderQueryKey = (
	policyholderLocator: string,
	authToken: string,
) => ['policies', policyholderLocator, authToken];

export const usePoliciesForPolicyholder = (
	policyholderLocator: string,
	authToken: string,
	disabled = false,
) =>
	useQuery({
		queryFn: async () => {
			const policiesResponse = await PolicyholderApi.getPoliciesForPolicyholder(
				authToken,
				policyholderLocator,
			);
			const graceLapses = policiesResponse.data?.map((p) => {
				if (hasOverdueInvoices(p)) {
					return PolicyApi.getGraceLapseAndReinstatementInfo(
						authToken,
						p.locator,
					);
				}
				return null;
			});
			const gLs = await axios.all(graceLapses);

			// filter out any graceLapse responses that are null or have returned an empty array
			const filteredGLs = gLs
				.filter((x) => x?.data?.length)
				.map((y) => y.data[0]);

			return {
				policies: policiesResponse.data,
				graceLapseReinstatementData: filteredGLs,
			};
		},
		queryKey: getUsePoliciesForPolicyholderQueryKey(
			policyholderLocator,
			authToken,
		),
		enabled: !!policyholderLocator && !disabled,
	});

const getUsePolicyholderQueryKey = (
	policyholderLocator: string,
	authToken: string,
) => ['policyholder', policyholderLocator, authToken];

export const usePolicyholder = (
	policyholderLocator: string,
	authToken: string,
) => {
	const queryKey = getUsePolicyholderQueryKey(policyholderLocator, authToken);
	return useQuery({
		queryFn: () =>
			PolicyholderApi.getPolicyholder(authToken, policyholderLocator),
		queryKey,
		enabled: !!policyholderLocator,
	});
};

export const useUpdatePolicyholderMutation = (
	policyholderLocator: string,
	authToken: string,
	queryClient: QueryClient,
) =>
	useMutation({
		mutationFn: (request: PolicyholderUpdateRequest) =>
			PolicyholderApi.updatePolicyholder(authToken, request),
		onSuccess: () => {
			const queryKey = getUsePolicyholderQueryKey(
				policyholderLocator,
				authToken,
			);
			queryClient.invalidateQueries(queryKey);
			successToast('Policyholder updated successfully');
		},
		onError: (e) => {
			if (axios.isAxiosError(e)) {
				errorToast('Error updating policyholder: ' + e.response?.data?.message);
				return;
			} else {
				errorToast('Error updating policyholder.');
			}
		},
	});

export const getInvoicesForPolicyholderQueryKey = (
	authorizationToken: string,
	policyholderLocator: string,
) => ['invoices', policyholderLocator, authorizationToken];

export const useInvoicesForPolicyholder = (
	authorizationToken: string,
	policyholderLocator: string,
	hasPermission: boolean,
) => {
	const queryKey = getInvoicesForPolicyholderQueryKey(
		authorizationToken,
		policyholderLocator,
	);
	return useQuery({
		queryFn: async () =>
			PolicyholderApi.getInvoicesForPolicyholder(
				authorizationToken,
				policyholderLocator,
			),
		queryKey,
		enabled: !!policyholderLocator && hasPermission,
	});
};
